import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import initZodLocale from '@/i18n/zodLocale.ts';
import { Box, Grid } from '@mui/material';

import 'app/styles/tailwind.css';
import 'app/styles/style.css';
import { connectVadWebsocket } from 'app/utils/vadWebsocket.ts';
import * as UserSelectors from 'app/selectors/userSelectors.ts';
import { LoadingStatus } from 'app/store/types.ts';
import Header from 'app/components/Header/Header.tsx';
import HeiHeader from './components/Header/HeiHeader.tsx';
import { useIsHeiDomain } from 'app/hooks/useIsHeiDomain.ts';
import { initializeApiClients } from 'app/apis/api.ts';
import AppContainerContent from 'app/AppContainerContent.tsx';
import DefaultLoadingPage from 'app/components/DefaultLoadingPage.tsx';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { UserActions } from './actions/user/userAction.ts';
import theme from './styles/theme.ts';

const AppContainer: React.FC = () => {
  const { instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const dispatch = useDispatch();
  const isHeiDomain = useIsHeiDomain();

  useEffect(() => {
    if (!isAuthenticated || !instance) return;

    initZodLocale();
    initializeApiClients(instance);
    dispatch(UserActions.getCurrentUser());
  }, [instance, isAuthenticated, dispatch]);

  const currentUser = useSelector(UserSelectors.CurrentUserSelector);

  // WebSocket connection that runs when currentUser is available
  useEffect(() => {
    if (currentUser) {
      // Queue WebSocket connection as a microtask
      queueMicrotask(() => {
        connectVadWebsocket();
      });
    }
  }, [currentUser]); // Run when currentUser changes

  const currentUserNotFound = useSelector(UserSelectors.CurrentUserNotFoundSelector);
  const loadingStatus = useSelector(UserSelectors.CurrentUserLoadingStatusSelector);

  if (!isAuthenticated && !currentUserNotFound && loadingStatus === LoadingStatus.Loading) {
    return <DefaultLoadingPage />;
  }

  return (
    <Grid container direction="column" component={Box} height="100vh">
      <Grid
        item
        sx={{
          width: '100%',
          height: isHeiDomain ? 0 : theme.mixins.denseToolbar.minHeight
        }}
      >
        {isHeiDomain ? <HeiHeader /> : <Header />}
      </Grid>
      <Grid
        item
        xs
        sx={{ width: '100%', height: isHeiDomain ? '100vh' : `calc(100vh - ${theme.mixins.denseToolbar.minHeight}px)` }}
      >
        <AppContainerContent isHeiDomain={isHeiDomain} />
      </Grid>
    </Grid>
  );
};

export default AppContainer;
